import { useState, useEffect, useCallback } from 'react';
import { reconciliationAPI as bankAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Bank, UploadSimple, DownloadSimple, ArrowsClockwise, MagnifyingGlass,
  CheckCircle, XCircle, Clock, Receipt, Funnel, Eye, Calendar,
  CaretLeft, CaretRight, FileXls
} from '@phosphor-icons/react';

const fmt = (v) => formatCurrency(v || 0);

export default function BankReconciliationPage() {
  const [activeView, setActiveView] = useState('statements');
  const [statements, setStatements] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [txnLoading, setTxnLoading] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [uploadForm, setUploadForm] = useState({ bank: 'Federal Bank', accountNumber: '', file: null });
  const [txnFilters, setTxnFilters] = useState({ reconciled: 'all', type: 'all', startDate: '', endDate: '', search: '' });
  const [txnPage, setTxnPage] = useState(0);
  const [txnTotal, setTxnTotal] = useState(0);

  const loadStatements = useCallback(async () => {
    try {
      const res = await bankAPI.listStatements();
      setStatements(res.data.statements || []);
    } catch {}
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const res = await bankAPI.getSummary();
      setSummary(res.data);
    } catch {}
  }, []);

  const loadTransactions = useCallback(async () => {
    setTxnLoading(true);
    try {
      const params = { limit: 100, skip: txnPage * 100 };
      if (txnFilters.reconciled !== 'all') params.reconciled = txnFilters.reconciled === 'yes';
      if (txnFilters.startDate) params.start_date = txnFilters.startDate;
      if (txnFilters.endDate) params.end_date = txnFilters.endDate;
      const res = await bankAPI.listTransactions(params);
      setTransactions(res.data.transactions || []);
      setTxnTotal(res.data.total || 0);
    } catch { toast.error('Failed to load transactions'); }
    finally { setTxnLoading(false); }
  }, [txnPage, txnFilters]);

  useEffect(() => {
    Promise.all([loadStatements(), loadSummary()]).finally(() => setLoading(false));
  }, [loadStatements, loadSummary]);

  useEffect(() => {
    if (activeView === 'transactions') loadTransactions();
  }, [activeView, loadTransactions]);

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.accountNumber) {
      toast.error('Please select a file and enter account number');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      const res = await bankAPI.uploadStatement(formData, uploadForm.bank, uploadForm.accountNumber);
      toast.success(`Uploaded! ${res.data.transactions_count || 0} transactions processed.`);
      setUploadDialog(false);
      setUploadForm({ bank: 'Federal Bank', accountNumber: '', file: null });
      loadStatements();
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (stmt) => {
    setDownloadingId(stmt.id);
    try {
      const res = await bankAPI.downloadStatement(stmt.id);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', stmt.filename || `statement_${stmt.id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch (e) {
      const msg = e.response?.status === 404 ? 'File not available for download (uploaded before download feature was added)' : 'Download failed';
      toast.error(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleAutoReconcile = async () => {
    setReconciling(true);
    try {
      const res = await bankAPI.autoMatch();
      toast.success(res.data.message || 'Reconciliation complete');
      loadSummary();
      if (activeView === 'transactions') loadTransactions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reconciliation failed'); }
    finally { setReconciling(false); }
  };

  return (
    <div className="space-y-5" data-testid="bank-reconciliation-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Bank Reconciliation</h1>
          <p className="text-sm text-[#64748B]">Upload statements, track transactions, reconcile with Zoho</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setUploadDialog(true)} data-testid="upload-statement-btn">
            <UploadSimple size={16} className="mr-2" /> Upload New Statement
          </Button>
          <Button onClick={handleAutoReconcile} disabled={reconciling} variant="outline" data-testid="auto-reconcile-btn">
            <ArrowsClockwise size={16} className={`mr-2 ${reconciling ? 'animate-spin' : ''}`} />
            {reconciling ? 'Reconciling...' : 'Auto Reconcile'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-5 gap-4">
          <div className="stats-card" data-testid="bank-total-txns">
            <div className="flex items-center gap-2"><Bank size={18} className="text-[#64748B]" /><p className="stats-card-label">Total Transactions</p></div>
            <p className="text-2xl font-bold mt-1">{(summary.total_transactions || 0).toLocaleString()}</p>
          </div>
          <div className="stats-card" data-testid="bank-reconciled">
            <div className="flex items-center gap-2"><CheckCircle size={18} className="text-green-600" /><p className="stats-card-label">Reconciled</p></div>
            <p className="text-2xl font-bold text-green-600 mt-1">{(summary.reconciled || 0).toLocaleString()}</p>
          </div>
          <div className="stats-card" data-testid="bank-unreconciled">
            <div className="flex items-center gap-2"><Clock size={18} className="text-amber-600" /><p className="stats-card-label">Unreconciled</p></div>
            <p className="text-2xl font-bold text-amber-600 mt-1">{(summary.unreconciled || 0).toLocaleString()}</p>
          </div>
          <div className="stats-card" data-testid="bank-recon-rate">
            <p className="stats-card-label">Reconciliation Rate</p>
            <p className="text-2xl font-bold mt-1">{typeof summary.reconciliation_rate === 'number' ? `${summary.reconciliation_rate.toFixed(1)}%` : (summary.reconciliation_rate || '0%')}</p>
            <div className="mt-1 bg-[#E2E8F0] rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${typeof summary.reconciliation_rate === 'number' ? summary.reconciliation_rate : parseFloat(summary.reconciliation_rate) || 0}%` }} />
            </div>
          </div>
          <div className="stats-card" data-testid="bank-statements-count">
            <div className="flex items-center gap-2"><FileXls size={18} className="text-[#64748B]" /><p className="stats-card-label">Statements Uploaded</p></div>
            <p className="text-2xl font-bold mt-1">{statements.length}</p>
          </div>
        </div>
      )}

      {/* View Switcher */}
      <div className="flex gap-1 bg-[#F8FAFC] p-1 rounded-lg w-fit" data-testid="bank-view-switcher">
        {[['statements', 'Uploaded Statements'], ['transactions', 'All Transactions']].map(([k, label]) => (
          <button key={k} onClick={() => setActiveView(k)} data-testid={`bank-view-${k}`}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${activeView === k ? 'bg-white text-[#0F4C81] font-semibold shadow-sm' : 'text-[#64748B] hover:text-[#0F4C81]'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Uploaded Statements View ── */}
      {activeView === 'statements' && (
        <div className="space-y-4">
          {loading ? <div className="text-center py-8 text-[#64748B]">Loading...</div> :
            statements.length === 0 ? (
              <div className="text-center py-12 bg-white border border-[#E2E8F0] rounded-lg">
                <Bank size={48} className="mx-auto mb-3 text-[#E2E8F0]" />
                <p className="text-[#64748B] mb-4">No bank statements uploaded yet</p>
                <Button onClick={() => setUploadDialog(true)}>
                  <UploadSimple size={16} className="mr-2" /> Upload First Statement
                </Button>
              </div>
            ) : (
              <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="statements-table">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC]">
                    <tr>
                      <th className="px-4 py-3 text-left">Filename</th>
                      <th className="px-4 py-3 text-left">Bank</th>
                      <th className="px-4 py-3 text-left">Account</th>
                      <th className="px-4 py-3 text-center">Transactions</th>
                      <th className="px-4 py-3 text-left">Date Range</th>
                      <th className="px-4 py-3 text-left">Uploaded</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statements.map(stmt => (
                      <tr key={stmt.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileXls size={18} className="text-green-600 flex-shrink-0" />
                            <span className="font-medium truncate max-w-[250px]">{stmt.filename}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[#64748B]">{stmt.bank}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{stmt.account_number || '—'}</td>
                        <td className="px-4 py-3 text-center font-semibold">{stmt.transaction_count?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">
                          {stmt.date_range?.from && `${stmt.date_range.from} — ${stmt.date_range.to}`}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">
                          {stmt.uploaded_at ? new Date(stmt.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleDownload(stmt)}
                            disabled={downloadingId === stmt.id}
                            data-testid={`download-stmt-${stmt.id}`}
                            className="h-8"
                          >
                            <DownloadSimple size={16} className="mr-1" />
                            {downloadingId === stmt.id ? 'Downloading...' : 'Download'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      )}

      {/* ── All Transactions View ── */}
      {activeView === 'transactions' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-xs text-[#64748B]">Status</label>
              <Select value={txnFilters.reconciled} onValueChange={v => { setTxnFilters(f => ({...f, reconciled: v})); setTxnPage(0); }}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Reconciled</SelectItem>
                  <SelectItem value="no">Unreconciled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#64748B]">From</label>
              <Input type="date" value={txnFilters.startDate} onChange={e => setTxnFilters(f => ({...f, startDate: e.target.value}))} className="w-40" />
            </div>
            <div>
              <label className="text-xs text-[#64748B]">To</label>
              <Input type="date" value={txnFilters.endDate} onChange={e => setTxnFilters(f => ({...f, endDate: e.target.value}))} className="w-40" />
            </div>
            <Button variant="outline" size="sm" onClick={() => { setTxnPage(0); loadTransactions(); }}>
              <Funnel size={14} className="mr-1" /> Apply
            </Button>
            <span className="text-sm text-[#64748B] ml-2">{txnTotal.toLocaleString()} transactions</span>
          </div>

          {/* Transactions table */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="transactions-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC]">
                  <tr>
                    <th className="px-3 py-2.5 text-left">Date</th>
                    <th className="px-3 py-2.5 text-left">Description</th>
                    <th className="px-3 py-2.5 text-right">Amount</th>
                    <th className="px-3 py-2.5 text-center">Type</th>
                    <th className="px-3 py-2.5 text-center">Status</th>
                    <th className="px-3 py-2.5 text-left">Category / Match</th>
                  </tr>
                </thead>
                <tbody>
                  {txnLoading ? <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">Loading...</td></tr> :
                    transactions.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">No transactions found</td></tr> :
                      transactions.map(txn => (
                        <tr key={txn.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                          <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{txn.date}</td>
                          <td className="px-3 py-2 text-xs max-w-[350px] truncate" title={txn.description}>{txn.description}</td>
                          <td className={`px-3 py-2 text-right font-semibold text-xs ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {txn.type === 'credit' ? '+' : '-'}{fmt(txn.amount)}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${txn.type === 'credit' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                              {txn.type}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {txn.reconciled
                              ? <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">Reconciled</span>
                              : <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">Pending</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-[#64748B]">
                            {txn.expense_category || txn.match_source || '—'}
                          </td>
                        </tr>
                      ))
                  }
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {txnTotal > 100 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" disabled={txnPage <= 0} onClick={() => setTxnPage(p => p - 1)}>
                <CaretLeft size={14} /> Prev
              </Button>
              <span className="text-sm text-[#64748B]">Page {txnPage + 1} of {Math.ceil(txnTotal / 100)}</span>
              <Button variant="outline" size="sm" disabled={(txnPage + 1) * 100 >= txnTotal} onClick={() => setTxnPage(p => p + 1)}>
                Next <CaretRight size={14} />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Upload Dialog ── */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-md" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UploadSimple size={20} /> Upload Bank Statement
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Bank</label>
              <Select value={uploadForm.bank} onValueChange={v => setUploadForm(f => ({...f, bank: v}))}>
                <SelectTrigger data-testid="upload-bank-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Federal Bank">Federal Bank</SelectItem>
                  <SelectItem value="HDFC Bank">HDFC Bank</SelectItem>
                  <SelectItem value="ICICI Bank">ICICI Bank</SelectItem>
                  <SelectItem value="SBI">SBI</SelectItem>
                  <SelectItem value="Axis Bank">Axis Bank</SelectItem>
                  <SelectItem value="Kotak Mahindra Bank">Kotak Mahindra Bank</SelectItem>
                  <SelectItem value="Yes Bank">Yes Bank</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Account Number</label>
              <Input
                placeholder="e.g. 11550200025434"
                value={uploadForm.accountNumber}
                onChange={e => setUploadForm(f => ({...f, accountNumber: e.target.value}))}
                data-testid="upload-account-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Statement File</label>
              <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-6 text-center hover:border-[#0F4C81] transition-colors">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={e => setUploadForm(f => ({...f, file: e.target.files[0]}))}
                  className="hidden"
                  id="statement-file-input"
                  data-testid="upload-file-input"
                />
                <label htmlFor="statement-file-input" className="cursor-pointer">
                  {uploadForm.file ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileXls size={24} className="text-green-600" />
                      <div className="text-left">
                        <p className="font-medium text-sm text-[#0F172A]">{uploadForm.file.name}</p>
                        <p className="text-xs text-[#64748B]">{(uploadForm.file.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <UploadSimple size={32} className="mx-auto mb-2 text-[#64748B]" />
                      <p className="text-sm text-[#64748B]">Click to select CSV or XLSX file</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">Supported: .csv, .xlsx, .xls</p>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadForm.file || !uploadForm.accountNumber} data-testid="upload-submit-btn">
              {uploading ? 'Uploading...' : 'Upload & Process'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
